/**
 * Painel Berytus › Documentos das farmácias: fila de análise dos documentos sanitários enviados pelas contas
 * (licença sanitária, CRT, AFE, SNGPC). Ver o arquivo, aprovar (pode ajustar a validade) ou recusar com motivo.
 * A habilitação da conta (nível 1 e 2) sai dos aprovados e vigentes.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';

interface Doc { id: string; tenantId: string; tenantName: string; kind: string; label: string; number: string | null; issuedAt: string | null; expiresAt: string | null;
  renewalProtocol: boolean; fileName: string; contentType: string; bytes: number; status: string; rejectReason: string | null; createdAt: string; createdBy: string | null;
  reviewedAt: string | null; reviewedBy: string | null }
const TABS: [string, string][] = [['pending_review', 'Em análise'], ['approved', 'Aprovados'], ['rejected', 'Recusados'], ['all', 'Todos']];
const TONE: Record<string, string> = { pending_review: 'info', approved: 'ok', rejected: 'err' };
const br = (d: string | null) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');

export function AdminDocuments() {
  const [status, setStatus] = useState('pending_review');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'documents', status], queryFn: () => api<{ items: Doc[] }>(`/admin/documents?status=${status}`), refetchInterval: 60_000 });
  const review = useMutation({
    mutationFn: (v: { d: Doc; decision: 'approve' | 'reject'; reason?: string; expiresAt?: string }) =>
      api(`/admin/documents/${v.d.tenantId}/${v.d.id}/review`, { method: 'POST', json: { decision: v.decision, reason: v.reason, expiresAt: v.expiresAt } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'documents'] }),
  });
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Documentos das farmácias</h1>
          <p>Confira o arquivo e aprove ou recuse com o motivo (o cliente vê o motivo). Habilitação: nível 1 = licença sanitária + CRT + AFE; nível 2 = + SNGPC.</p>
        </div>
      </div>
      <div className="seg" role="group" aria-label="Situação" style={{ marginBottom: 12 }}>
        {TABS.map(([v, l]) => <button key={v} type="button" aria-pressed={status === v} onClick={() => setStatus(v)}>{l}</button>)}
      </div>
      {review.error && <ErrorBox error={review.error} />}
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : !q.data.items.length ? <div className="empty"><b>Nada aqui</b>Nenhum documento nesta situação.</div> : (
        <section className="panel flush">
          <div className="grid-wrap">
            <table className="grid" style={{ width: '100%', minWidth: 900 }}>
              <thead><tr><th>Conta</th><th>Documento</th><th>Número</th><th>Validade</th><th>Enviado</th><th>Situação</th><th /></tr></thead>
              <tbody>
                {q.data.items.map((d) => (
                  <tr key={d.id} style={{ cursor: 'default' }}>
                    <td><b>{d.tenantName}</b></td>
                    <td>{d.label}{d.renewalProtocol && <div className="small muted">protocolo de renovação</div>}</td>
                    <td className="mono small">{d.number ?? '—'}</td>
                    <td>{br(d.expiresAt)}</td>
                    <td className="small">{dateTime(d.createdAt)}{d.createdBy ? <div className="muted">{d.createdBy}</div> : null}</td>
                    <td><Tag tone={TONE[d.status] ?? 'off'}>{d.status === 'pending_review' ? 'em análise' : d.status === 'approved' ? 'aprovado' : d.status === 'rejected' ? 'recusado' : d.status}</Tag>
                      {d.rejectReason && <div className="small" style={{ color: 'var(--err)' }}>{d.rejectReason}</div>}
                      {d.reviewedBy && <div className="small muted">{d.reviewedBy} · {dateTime(d.reviewedAt)}</div>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <a className="btn sm quiet" href={`/api/v1/admin/documents/${d.tenantId}/${d.id}/file`} target="_blank" rel="noreferrer">Ver arquivo</a>
                        {d.status === 'pending_review' && <>
                          <button type="button" className="btn sm" disabled={review.isPending} onClick={() => {
                            const v = window.prompt(`Aprovar ${d.label} de ${d.tenantName}. Validade (AAAA-MM-DD; deixe como está se estiver certa):`, d.expiresAt ?? '');
                            if (v == null) return;
                            if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v.trim())) { window.alert('Data no formato AAAA-MM-DD.'); return; }
                            review.mutate({ d, decision: 'approve', expiresAt: v.trim() || undefined });
                          }}>Aprovar</button>
                          <button type="button" className="btn sm danger" disabled={review.isPending} onClick={() => {
                            const r = window.prompt(`Motivo da recusa (o cliente vê):`);
                            if (r && r.trim().length >= 3) review.mutate({ d, decision: 'reject', reason: r.trim() });
                          }}>Recusar</button>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
